import {
	TrackerSignalPriority,
	TrackerSignalResultStatus,
} from "../../../schema/trackerSignalResult";

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago", "1 week ago")
 */
export const getRelativeTimeString = (date: string | Date): string => {
	const dateObj = typeof date === "string" ? new Date(date) : date;
	const diffMs = Date.now() - dateObj.getTime();
	const diffSeconds = Math.floor(diffMs / 1000);
	const diffMinutes = Math.floor(diffSeconds / 60);
	const diffHours = Math.floor(diffMinutes / 60);
	const diffDays = Math.floor(diffHours / 24);
	const diffWeeks = Math.floor(diffDays / 7);
	const diffMonths = Math.floor(diffDays / 30);
	const diffYears = Math.floor(diffDays / 365);

	if (diffMinutes < 1) {
		return "just now";
	}

	if (diffHours < 1) {
		return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`;
	}

	if (diffDays < 1) {
		return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
	}

	if (diffDays < 7) {
		return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
	}

	if (diffDays < 30) {
		return `${diffWeeks} ${diffWeeks === 1 ? "week" : "weeks"} ago`;
	}

	if (diffDays < 365) {
		return `${diffMonths} ${diffMonths === 1 ? "month" : "months"} ago`;
	}

	return `${diffYears} ${diffYears === 1 ? "year" : "years"} ago`;
};

/**
 * Get short label for signal priority
 */
export const getSignalPriorityLabel = (priority: string | null | undefined): string => {
	if (!priority) {
		return "";
	}

	// Legacy job-change rows stored bare digits until PREC-4677; keep tolerating unrepaired rows.
	if (priority === "1" || priority === TrackerSignalPriority.PRIORITY_1) {
		return "Priority 1";
	}

	if (priority === "2" || priority === TrackerSignalPriority.PRIORITY_2) {
		return "Priority 2";
	}

	if (priority === "3" || priority === TrackerSignalPriority.PRIORITY_3) {
		return "Priority 3";
	}

	if (
		priority.toLowerCase() === "disqualified" ||
		priority === TrackerSignalPriority.DISQUALIFIED
	) {
		return "Disqualified";
	}

	return priority;
};

/**
 * Get color classes for signal category badge
 */
export const getSignalCategoryColor = (category: string | null | undefined): string => {
	if (!category) {
		return "bg-gray-100 text-gray-500 border-gray-300";
	}

	if (category === "Industry update") {
		return "bg-gray-100 text-gray-800 border-gray-300";
	}

	if (category === "Lead update") {
		return "bg-blue-100 text-blue-800 border-blue-300";
	}

	if (category === "Company update") {
		return "bg-purple-100 text-purple-800 border-purple-300";
	}

	return "bg-gray-100 text-gray-500 border-gray-300";
};

/**
 * Get color classes for signal priority badge
 */
export const getSignalPriorityColor = (priority: string | null | undefined): string => {
	if (!priority) {
		return "bg-gray-100 text-gray-500 border-gray-300";
	}

	if (priority === "1" || priority === TrackerSignalPriority.PRIORITY_1) {
		return "bg-background-success text-text-success border-text-success";
	}

	if (priority === "2" || priority === TrackerSignalPriority.PRIORITY_2) {
		return "bg-background-warning text-text-warning border-text-warning";
	}

	if (priority === "3" || priority === TrackerSignalPriority.PRIORITY_3) {
		return "bg-error-light text-error border-error";
	}

	if (
		priority.toLowerCase() === "disqualified" ||
		priority === TrackerSignalPriority.DISQUALIFIED
	) {
		return "bg-error text-white border-error";
	}

	return "bg-gray-100 text-gray-500 border-gray-300";
};

/**
 * Get display label for signal status
 */
export const getSignalStatusLabel = (status: string): string => {
	switch (status) {
		case "PENDING":
		case TrackerSignalResultStatus.PENDING:
			return "Pending";
		case "COLLECTED":
		case TrackerSignalResultStatus.COLLECTED:
			return "Collected";
		case "SCORED":
		case TrackerSignalResultStatus.SCORED:
			return "Scored";
		case "COMPLETED":
		case TrackerSignalResultStatus.COMPLETED:
			return "Completed";
		case "ERROR":
		case TrackerSignalResultStatus.ERROR:
			return "Error";
		default:
			return status;
	}
};

/**
 * Get color classes for signal status badge
 */
export const getSignalStatusColor = (status: string): string => {
	switch (status) {
		case "PENDING":
		case TrackerSignalResultStatus.PENDING:
			return "bg-blue-100 text-blue-700";
		case "COLLECTED":
		case TrackerSignalResultStatus.COLLECTED:
			return "bg-purple-100 text-purple-700";
		case "SCORED":
		case TrackerSignalResultStatus.SCORED:
			return "bg-green-100 text-green-700";
		case "COMPLETED":
		case TrackerSignalResultStatus.COMPLETED:
			return "bg-emerald-100 text-emerald-700";
		case "ERROR":
		case TrackerSignalResultStatus.ERROR:
			return "bg-red-100 text-red-700";
		default:
			return "bg-gray-100 text-gray-500";
	}
};
